/**
 * Auth Helpers — Authentication utilities for the account system
 *
 * Provides:
 * - Password hashing (SHA-256, symbolic/local)
 * - Link code generation & lookup
 * - Session management helpers
 * - Login validation
 *
 * @moved from src/utils/authHelpers.ts (R19 — Fase 2 continuation)
 */

import { AccountsState, Account, isLearnerAccount } from '../types'
import {
  PASSWORD_MIN_LENGTH,
  SESSION_TIMEOUT_MS,
  LINK_CODE_MAX_RETRIES,
  DISPLAY_NAME_MAX_LENGTH,
  DISPLAY_NAME_MIN_LENGTH,
} from '../../config/constants'
import { generateRandomCode } from '../../utils/randomCode'

const HASH_ALGO_PREFIX_SHA256 = 'sha256-v1:'
const HASH_ALGO_PREFIX_FNV = 'fnv-v1:'

/**
 * Compute the SHA-256 hex digest of a string using the Web Crypto API.
 * Throws if crypto.subtle is unavailable (e.g. non-secure context like file://).
 *
 * @internal — exported only for migration helpers; consumers should use hashPassword.
 */
async function digestSha256Hex(input: string): Promise<string> {
  if (typeof crypto === 'undefined' || !crypto.subtle) {
    throw new Error(
      'Web Crypto API niet beschikbaar. Open de app via HTTPS of localhost.',
    )
  }
  const encoder = new TextEncoder()
  const data = encoder.encode(input)
  const hashBuffer = await crypto.subtle.digest('SHA-256', data)
  const hashArray = Array.from(new Uint8Array(hashBuffer))
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('')
}

/**
 * Hash a password (symbolic, client-side only).
 *
 * Returns a versioned, prefixed hash string:
 *   - `sha256-v1:<64-hex>` when Web Crypto is available (HTTPS/localhost).
 *   - Throws otherwise — we no longer silently downgrade to the FNV fallback,
 *     which provided dangerously weak protection without informing the user.
 *
 * NOT real security — just prevents plain-text storage in localStorage.
 *
 * // FASE3: server-side auth required
 * In Fase 3 (backend migration), this client-side hashing MUST be removed.
 * Passwords should be sent over HTTPS and hashed server-side using bcrypt/argon2.
 *
 * Backwards-compat:
 *   `verifyPassword` accepts both new prefixed hashes and legacy un-prefixed
 *   hashes (64-char hex assumed sha256, 32-char hex assumed legacy-fnv).
 *   Legacy hashes will be upgraded transparently on next successful login.
 */
export async function hashPassword(password: string): Promise<string> {
  const digest = await digestSha256Hex(password)
  return `${HASH_ALGO_PREFIX_SHA256}${digest}`
}

/**
 * Internal: compute the digest portion only (no prefix) using the algorithm
 * indicated by a stored hash's prefix. Used by verifyPassword to support
 * legacy un-prefixed hashes.
 *
 * Note: the FNV path is preserved ONLY for verifying old stored hashes so
 * existing users can still log in once. It will never be used to PRODUCE
 * new hashes.
 */
function legacyFnvDigest(password: string): string {
  let h1 = 0x811c9dc5 >>> 0
  let h2 = 0x811c9dc5 >>> 0
  for (let i = 0; i < password.length; i++) {
    const c = password.charCodeAt(i)
    h1 = Math.imul(h1 ^ c, 0x01000193) >>> 0
    h2 = Math.imul(h2 ^ (c + i + 0x9e), 0x01000193) >>> 0
  }
  return (
    h1.toString(16).padStart(8, '0') +
    h2.toString(16).padStart(8, '0') +
    h1.toString(16).padStart(8, '0') +
    h2.toString(16).padStart(8, '0')
  )
}

/**
 * Verify a password against a stored hash. Supports:
 *   - New prefixed hashes:   `sha256-v1:<hex>` or `fnv-v1:<hex>` (verify only).
 *   - Legacy un-prefixed hashes:
 *       64-char hex → treated as legacy sha256
 *       32-char hex → treated as legacy fnv-1a fallback
 *
 * // FASE3: server-side auth required
 * In Fase 3, password verification MUST happen on the server.
 * The client will receive a session token (JWT) upon successful login.
 */
export async function verifyPassword(
  password: string,
  storedHash: string,
): Promise<boolean> {
  // 1. New prefixed format
  if (storedHash.startsWith(HASH_ALGO_PREFIX_SHA256)) {
    const stored = storedHash.slice(HASH_ALGO_PREFIX_SHA256.length)
    try {
      const computed = await digestSha256Hex(password)
      return computed === stored
    } catch {
      return false
    }
  }
  if (storedHash.startsWith(HASH_ALGO_PREFIX_FNV)) {
    const stored = storedHash.slice(HASH_ALGO_PREFIX_FNV.length)
    return legacyFnvDigest(password) === stored
  }
  // 2. Legacy un-prefixed format (best-effort detection by length)
  if (storedHash.length === 64) {
    try {
      const computed = await digestSha256Hex(password)
      return computed === storedHash
    } catch {
      return false
    }
  }
  if (storedHash.length === 32) {
    return legacyFnvDigest(password) === storedHash
  }
  // Unknown format — fail closed.
  return false
}

/**
 * Returns true if a stored hash is in the legacy un-prefixed format.
 * Consumers can use this to opportunistically re-hash on next successful login.
 */
export function isLegacyPasswordHash(storedHash: string): boolean {
  return (
    !storedHash.startsWith(HASH_ALGO_PREFIX_SHA256) &&
    !storedHash.startsWith(HASH_ALGO_PREFIX_FNV)
  )
}

/**
 * Generate a unique 6-digit link code for a learner.
 * Used by teachers to let students join their class.
 * Delegates to shared generateRandomCode() (D-SCAN-2 D2-2).
 */
export function generateLinkCode(): string {
  return generateRandomCode(6)
}

/**
 * Find an account by link code.
 */
export function findAccountByLinkCode(
  accounts: AccountsState,
  linkCode: string,
): Account | null {
  const normalizedCode = linkCode.toUpperCase().trim()
  const allAccounts = Object.values(accounts.accountsById)
  return (
    allAccounts.find(
      (a) => isLearnerAccount(a) && a.linkCode === normalizedCode,
    ) || null
  )
}

/**
 * Find an account by username (for learner login).
 */
export function findAccountByUsername(
  accounts: AccountsState,
  username: string,
): Account | null {
  const normalizedUsername = username.toLowerCase().trim()
  const allAccounts = Object.values(accounts.accountsById)
  return (
    allAccounts.find(
      (a) =>
        isLearnerAccount(a) && a.username?.toLowerCase() === normalizedUsername,
    ) || null
  )
}

/**
 * Find an account by email (for parent/teacher/admin login).
 */
export function findAccountByEmail(
  accounts: AccountsState,
  email: string,
): Account | null {
  const normalizedEmail = email.toLowerCase().trim()
  const allAccounts = Object.values(accounts.accountsById)
  return (
    allAccounts.find((a) => a.email?.toLowerCase() === normalizedEmail) || null
  )
}

/**
 * Check if all accounts in state are demo accounts.
 */
export function isAllDemoAccounts(accounts: AccountsState): boolean {
  const allAccounts = Object.values(accounts.accountsById)
  if (allAccounts.length === 0) return false
  return allAccounts.every((a) => a.isDemo === true)
}

/**
 * Check if any production (non-demo) accounts exist.
 */
export function hasProductionAccounts(accounts: AccountsState): boolean {
  return Object.values(accounts.accountsById).some((a) => !a.isDemo)
}

/**
 * Generate a username suggestion from a display name.
 * E.g., "Djayden van Berg" → "djayden.vanberg"
 */
export function suggestUsername(displayName: string): string {
  return displayName
    .toLowerCase()
    .replace(/\s+/g, '.')
    .replace(/[^a-z0-9.]/g, '')
    .replace(/\.{2,}/g, '.')
    .replace(/^\.+|\.+$/g, '')
}

/**
 * Validate a username (3-20 chars, alphanumeric + dots).
 */
export function isValidUsername(username: string): boolean {
  return /^[a-z0-9.]{3,20}$/.test(username)
}

/**
 * Validate a password (min 4 chars for kids, min 6 for adults).
 */
export function isValidPassword(
  password: string,
  role: 'learner' | 'parent' | 'teacher' | 'admin' = 'learner',
): boolean {
  const minLength =
    role === 'learner'
      ? PASSWORD_MIN_LENGTH.learner
      : PASSWORD_MIN_LENGTH.default
  return password.length >= minLength
}

/**
 * Session timeout check (returns true if session has expired).
 * Default: 30 minutes for learners, 60 minutes for others.
 */
export function isSessionExpired(
  lastActive: number,
  role: 'learner' | 'parent' | 'teacher' | 'admin' = 'learner',
): boolean {
  const timeoutMs =
    role === 'learner' ? SESSION_TIMEOUT_MS.learner : SESSION_TIMEOUT_MS.default
  return Date.now() - lastActive > timeoutMs
}

/**
 * Check if a username is available (not already taken by another account).
 */
export function isUsernameAvailable(
  accounts: AccountsState,
  username: string,
  excludeAccountId?: string,
): boolean {
  const normalizedUsername = username.toLowerCase().trim()
  if (!normalizedUsername) return false
  return !Object.values(accounts.accountsById).some(
    (a) =>
      a.id !== excludeAccountId &&
      isLearnerAccount(a) &&
      a.username?.toLowerCase() === normalizedUsername,
  )
}

/**
 * Check if an email is available (not already taken by another account).
 */
export function isEmailAvailable(
  accounts: AccountsState,
  email: string,
  excludeAccountId?: string,
): boolean {
  const normalizedEmail = email.toLowerCase().trim()
  if (!normalizedEmail) return false
  return !Object.values(accounts.accountsById).some(
    (a) =>
      a.id !== excludeAccountId && a.email?.toLowerCase() === normalizedEmail,
  )
}

/**
 * Generate a unique 6-digit link code, checking for collisions.
 * Retries up to LINK_CODE_MAX_RETRIES times to avoid duplicates.
 *
 * @throws Error if no unique code can be generated within the retry budget.
 *         Caller (UI) should surface this as "Kon geen unieke koppelcode
 *         genereren — probeer opnieuw." instead of risking a silent collision.
 */
export function generateUniqueLinkCode(accounts: AccountsState): string {
  for (let attempt = 0; attempt < LINK_CODE_MAX_RETRIES; attempt++) {
    const code = generateLinkCode()
    const existing = findAccountByLinkCode(accounts, code)
    if (!existing) return code
  }
  throw new Error(
    `Kon geen unieke koppelcode genereren na ${LINK_CODE_MAX_RETRIES} pogingen. ` +
      'Dit kan duiden op een bug of een opvallend volle code-ruimte.',
  )
}

/**
 * Sanitize a display name (trim, collapse whitespace, limit length).
 */
export function sanitizeDisplayName(name: string): string {
  return name.trim().replace(/\s+/g, ' ').slice(0, DISPLAY_NAME_MAX_LENGTH)
}

/**
 * Sanitize an email address (trim, lowercase).
 */
export function sanitizeEmail(email: string): string {
  return email.trim().toLowerCase()
}

/**
 * Validate registration input comprehensively.
 * Returns null if valid, or an error message string if invalid.
 */
export function validateRegistrationInput(
  role: 'learner' | 'parent' | 'teacher',
  input: {
    name: string
    email?: string
    password: string
    schoolName?: string
  },
  accounts: AccountsState,
): string | null {
  // Name validation
  const sanitizedName = sanitizeDisplayName(input.name)
  if (!sanitizedName || sanitizedName.length < DISPLAY_NAME_MIN_LENGTH) {
    return `Naam moet minimaal ${DISPLAY_NAME_MIN_LENGTH} tekens bevatten.`
  }

  // Password validation
  const minLength =
    role === 'learner'
      ? PASSWORD_MIN_LENGTH.learner
      : PASSWORD_MIN_LENGTH.default
  if (input.password.length < minLength) {
    return `Wachtwoord moet minimaal ${minLength} tekens bevatten.`
  }

  // Email validation for non-learner roles
  if (role !== 'learner') {
    if (!input.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(input.email)) {
      return 'Vul een geldig e-mailadres in.'
    }
    if (!isEmailAvailable(accounts, input.email)) {
      return 'Dit e-mailadres is al in gebruik. Probeer in te loggen of gebruik een ander adres.'
    }
  }

  // Username uniqueness for learners
  if (role === 'learner') {
    const username = suggestUsername(sanitizedName)
    if (!isUsernameAvailable(accounts, username)) {
      return `De gebruikersnaam "${username}" is al in gebruik. Kies een andere naam.`
    }
  }

  // School name for teachers
  if (role === 'teacher') {
    if (!input.schoolName || input.schoolName.trim().length < 2) {
      return 'Vul de naam van je school in.'
    }
  }

  return null
}
